/**
 * DashboardLayout Component
 * Provides the main layout structure for the dashboard:
 * - Sidebar with navigation links
 * - Header with user info and logout
 * - Content area for nested routes
 */
import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './DashboardLayout.css';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  /**
   * Handles user logout — ends session and redirects to login page.
   */
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className={`dashboard-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* ===== Sidebar Navigation ===== */}
      <aside className="dashboard-sidebar">
        {/* Sidebar header / logo */}
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <svg width="32" height="32" viewBox="0 0 40 40" fill="none">
              <rect width="40" height="40" rx="10" fill="url(#sidebarLogoGrad)" />
              <path d="M12 28V16L20 12L28 16V28L20 32L12 28Z" stroke="white" strokeWidth="2" fill="none" />
              <path d="M20 12V32" stroke="white" strokeWidth="1.5" opacity="0.5" />
              <defs>
                <linearGradient id="sidebarLogoGrad" x1="0" y1="0" x2="40" y2="40">
                  <stop stopColor="#6366f1" />
                  <stop offset="1" stopColor="#8b5cf6" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          {!sidebarCollapsed && <span className="sidebar-brand">Amanak</span>}
        </div>

        {/* Navigation links */}
        <nav className="sidebar-nav">
          <NavLink to="/members" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="10" cy="6" r="3" />
              <path d="M3 17.5c0-3.5 3-6 7-6s7 2.5 7 6" />
            </svg>
            {!sidebarCollapsed && <span>Members</span>}
          </NavLink>

          <NavLink to="/teams" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="7" cy="7" r="2.5" />
              <circle cx="13" cy="7" r="2.5" />
              <path d="M1 16c0-3 2-5 6-5s6 2 6 5" />
              <path d="M13 11c2 0 4 1.2 4 3.5" />
            </svg>
            {!sidebarCollapsed && <span>Teams</span>}
          </NavLink>

          <NavLink to="/roles" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M10 2L12.5 7H17.5L13.5 10.5L15 16L10 13L5 16L6.5 10.5L2.5 7H7.5L10 2Z" />
            </svg>
            {!sidebarCollapsed && <span>Roles</span>}
          </NavLink>
        </nav>

        {/* Collapse toggle button */}
        <button
          className="sidebar-toggle"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5"
            style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s ease' }}>
            <path d="M11 4L6 9L11 14" />
          </svg>
        </button>
      </aside>

      {/* ===== Main Content Area ===== */}
      <div className="dashboard-main">
        {/* Top header bar */}
        <header className="dashboard-header">
          <div className="header-left">
            {/* Empty for now — can be used for breadcrumbs */}
          </div>
          <div className="header-right">
            {/* User info display */}
            <div className="header-user">
              <div className="header-avatar">
                {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="header-user-info">
                <span className="header-user-name">{user?.name || 'Admin'}</span>
                <span className="header-user-email">{user?.email}</span>
              </div>
            </div>
            {/* Logout button */}
            <button className="header-logout" onClick={handleLogout} title="Sign out">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M6.75 15.75H3.75C3.15 15.75 2.25 15.15 2.25 14.25V3.75C2.25 2.85 3.15 2.25 3.75 2.25H6.75" />
                <path d="M12 12.75L15.75 9L12 5.25" />
                <path d="M15.75 9H6.75" />
              </svg>
            </button>
          </div>
        </header>

        {/* Page content (rendered via nested routes) */}
        <main className="dashboard-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
